import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import {
  WorkspaceEnvironment,
  WorkspaceStatus,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { Badge, Card, CardContent, CardHeader, CardTitle, cn } from '@sunstone/ui';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router';

const STATUS_LABEL: Partial<Record<WorkspaceStatus, string>> = {
  [WorkspaceStatus.DEGRADED]: 'Degraded',
  [WorkspaceStatus.UNAVAILABLE]: 'Unavailable',
  [WorkspaceStatus.BLOCKED]: 'Blocked',
  [WorkspaceStatus.PROVISIONING_FAILED]: 'Provision failed',
};

const ENV_LABEL: Partial<Record<WorkspaceEnvironment, string>> = {
  [WorkspaceEnvironment.PRODUCTION]: 'Prod',
  [WorkspaceEnvironment.STAGING]: 'Stage',
  [WorkspaceEnvironment.DEVELOPMENT]: 'Dev',
};

interface AtRiskWorkspacesProps {
  workspaces: Workspace[];
  totalCount: number;
  isLoading: boolean;
}

export function AtRiskWorkspaces({ workspaces, totalCount, isLoading }: AtRiskWorkspacesProps) {
  const hasIssues = !isLoading && totalCount > 0;

  return (
    <Card className={cn('h-full flex flex-col', hasIssues && 'border-rose-500/30')}>
      <CardHeader className="pb-3 flex-shrink-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          {isLoading ? (
            <span className="text-muted-foreground">Checking for issues…</span>
          ) : hasIssues ? (
            <>
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <span className="text-rose-500">
                {totalCount} workspace{totalCount !== 1 ? 's' : ''} need attention
              </span>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span>All workspaces healthy</span>
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 overflow-y-auto max-h-[380px]">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : !hasIssues ? (
          <p className="text-sm text-muted-foreground">No workspaces currently need attention.</p>
        ) : (
          <ul className="space-y-1">
            {workspaces.slice(0, 5).map((ws) => {
              // Use ws.tenantId (the workspace's own tenant) so operator/partner
              // admins navigating cross-tenant workspaces land on the correct page.
              const href = `/tenants/${ws.tenantId}/deployments/${ws.deploymentId}/workspaces/${ws.id}`;
              const envLabel = ENV_LABEL[ws.environment] ?? '';
              const statusLabel = STATUS_LABEL[ws.status] ?? 'At risk';

              return (
                <li key={ws.id}>
                  <Link
                    to={href}
                    className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-block h-2 w-2 rounded-full flex-shrink-0 bg-rose-500" />
                      <span className="truncate font-medium">{ws.displayName || ws.id}</span>
                      {envLabel && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                          {envLabel}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{statusLabel}</span>
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                </li>
              );
            })}
            {totalCount > 5 && (
              <li>
                <Link
                  to="/workspaces"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2"
                >
                  + {totalCount - 5} more
                </Link>
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
